import {
  AppBreadcrumb,
  AppButton,
  AppCard,
  AppCheckbox,
  AppSelect,
  AppSpace,
  AppTextArea,
} from '@/components/atoms';
import AppAdminFormLabel from '@/components/atoms/AppAdminFormLabel';
import AppTable from '@/components/atoms/AppTable';
import { RA2NDatePicker } from '@/components/organisms';
import {
  MASSAGER_DELETE_VISIT_DATA_ERROR,
  parseClinicLocationId,
  useClinicLocationMassagerQuery,
  useClinicLocationQuery,
  useDeleteClinicLocationMassagerMutation,
  useUpdateClinicLocationMassagerMutation,
} from '@/hooks/admin/useClinicLocationCreate';
import { useStandardDeleteConfirm } from '@/hooks/useStandardDeleteConfirm';
import useAppToast from '@/hooks/useAppToast';
import { convGtJDate, dateValueToIso } from '@/utils/dateConverter';
import { partsToTime, timeToParts } from '@/utils/timeParts';
import { isValidKatakana } from '@/utils';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE, isNotFoundError } from '@/utils/apiError';
import {
  getClinicLocationMassagerScheduleSchema,
  AVAILABLE_TO_INDEFINITE,
  singleServiceScheduleSchema,
  type ClinicLocationMassagerScheduleFormData,
} from '@/schemas/clinicLocationMassagerScheduleSchema';
import { z } from 'zod';
import { useSiteAdminAuth } from '@/shared/providers/SiteAdminAuthContext';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { BreadcrumbItemType } from 'antd/es/breadcrumb/Breadcrumb';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, type ControllerRenderProps, type Path, type Resolver } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import type { TableProps } from 'antd';
import { WEEKDAY_LABELS_WITH_KEY } from '@/constants/weekday';
import {
  SERVICE_TYPE_A2,
  SERVICE_TYPE_BOTH,
  SERVICE_TYPE_R2,
} from '@/constants/serviceType';
import { HOUR_OPTIONS, MINUTE_OPTIONS } from '@/constants/timeOptions';
import { getManagerWarningMessage } from './managerWarningMessage';
import { LoadingTemplate, SiteAdminNotFoundTemplate } from '@/components/templates';

/** Massager profile eligibility; use any returned block when API omits the other service. */
function massagerQualifiesForServiceSide(
  massagerServiceType: string | null | undefined,
  side: typeof SERVICE_TYPE_A2 | typeof SERVICE_TYPE_R2
): boolean {
  const t = (massagerServiceType ?? '').toString().toUpperCase();
  if (side === SERVICE_TYPE_A2) {
    return t === SERVICE_TYPE_A2 || t === SERVICE_TYPE_BOTH;
  }
  return t === SERVICE_TYPE_R2 || t === SERVICE_TYPE_BOTH;
}

type ScheduleRow = {
  key: string;
  label: string;
  startKey: 'am_time_start' | 'am_time_end' | 'pm_time_start' | 'pm_time_end';
  endKey: 'am_time_start' | 'am_time_end' | 'pm_time_start' | 'pm_time_end';
  days: Record<string, keyof z.infer<typeof singleServiceScheduleSchema>>;
};

type TimeSelectValue = number | '' | undefined;

export default function EditClinicLocationMassagerPage() {
  const navigate = useNavigate();
  const toast = useAppToast();
  const { site, id, massagerId } = useParams<{ site: string; id: string; massagerId: string }>();
  const { currentSite } = useSiteAdminAuth();

  const siteType = site ?? currentSite ?? 'medix';
  const locationId = parseClinicLocationId(id ?? null);
  const massagerIdNum = massagerId ? parseInt(massagerId, 10) : null;

  const locationQuery = useClinicLocationQuery(siteType, id ?? null, {
    skipErrorToast: true,
  });
  const locationData = locationQuery.data?.data;
  const clinicServiceType = locationData?.service_type?.toUpperCase() ?? null;

  const massagerDetailQuery = useClinicLocationMassagerQuery(
    siteType,
    locationId ?? null,
    massagerIdNum
  );
  const dual = massagerDetailQuery.data?.data;
  const r2Assignment = dual?.R2;
  const a2Assignment = dual?.A2;

  const updateMutation = useUpdateClinicLocationMassagerMutation();
  const deleteMutation = useDeleteClinicLocationMassagerMutation();
  const openStandardDeleteConfirm = useStandardDeleteConfirm();
  const [showNotFound, setShowNotFound] = useState(false);

  const massagerInfo =
    massagerIdNum != null && a2Assignment != null && Number(a2Assignment.id) === massagerIdNum
      ? a2Assignment
      : massagerIdNum != null && r2Assignment != null && Number(r2Assignment.id) === massagerIdNum
        ? r2Assignment
        : (a2Assignment ?? r2Assignment);

  const locationOffersR2 =
    clinicServiceType === SERVICE_TYPE_R2 || clinicServiceType === SERVICE_TYPE_BOTH;
  const locationOffersA2 =
    clinicServiceType === SERVICE_TYPE_A2 || clinicServiceType === SERVICE_TYPE_BOTH;

  const massagerQualificationType = (a2Assignment ?? r2Assignment)?.massager_service_type ?? null;
  const isA2Qualified = massagerQualifiesForServiceSide(
    massagerQualificationType,
    SERVICE_TYPE_A2
  );
  const isR2Qualified = massagerQualifiesForServiceSide(
    massagerQualificationType,
    SERVICE_TYPE_R2
  );

  const r2Enabled = isR2Qualified && locationOffersR2;
  const a2Enabled = isA2Qualified && locationOffersA2;

  const availableTo = locationData?.available_to ?? null;
  const r2LastVisitDate = r2Assignment?.last_visit_date ?? null;
  const a2LastVisitDate = a2Assignment?.last_visit_date ?? null;

  const rawManagers = locationData?.managers || [];
  const managersForWarning = rawManagers.flatMap(m => {
    const entries = m.manager_entries;
    if (entries?.length) {
      return entries.map(e => ({
        service_type: e.service_type,
        available_to: e.available_to ?? m.available_to,
      }));
    }
    return [{ service_type: m.service_type, available_to: m.available_to }];
  });
  const managerWarningMsg = getManagerWarningMessage(
    managersForWarning,
    !!a2Assignment,
    !!r2Assignment
  );

  const breadcrumbItems: BreadcrumbItemType[] = [
    { title: '拠点一覧', href: `/${siteType}/admin/clinic_locations` },
    {
      title: ` 拠点: ${locationData?.serial_number}`,
      href: `/${siteType}/admin/clinic_locations/${locationId}`,
    },
    {
      title: ` 拠点施術者更新: ${massagerInfo?.massager_name}`,
    },
  ];

  const resolver: Resolver<ClinicLocationMassagerScheduleFormData> = useCallback(
    (values, context, options) => {
      const dynamicSchema = getClinicLocationMassagerScheduleSchema({
        availableTo,
        r2LastVisitDate,
        a2LastVisitDate,
        r2Enabled,
        a2Enabled,
      });
      return zodResolver(dynamicSchema)(values, context, options);
    },
    [availableTo, r2LastVisitDate, a2LastVisitDate, r2Enabled, a2Enabled]
  );

  const defaultServiceValues = {
    am_time_start: null,
    am_time_end: null,
    pm_time_start: null,
    pm_time_end: null,
    is_am_monday: false,
    is_am_tuesday: false,
    is_am_wednesday: false,
    is_am_thursday: false,
    is_am_friday: false,
    is_am_saturday: false,
    is_am_sunday: false,
    is_pm_monday: false,
    is_pm_tuesday: false,
    is_pm_wednesday: false,
    is_pm_thursday: false,
    is_pm_friday: false,
    is_pm_saturday: false,
    is_pm_sunday: false,
    available_from: null,
    available_to: null,
  };

  const form = useTrimmedForm<ClinicLocationMassagerScheduleFormData>({
    resolver,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      r2: defaultServiceValues,
      a2: defaultServiceValues,
      remarks: null as string | null,
    },
  });

  const { clearErrors, watch } = form;

  useEffect(() => {
    if (!r2Enabled) {
      form.clearErrors('r2');
    }
    if (!a2Enabled) {
      form.clearErrors('a2');
    }
  }, [r2Enabled, a2Enabled, form]);
  // Clear field error when user types (only show error when submit, clear when typing)
  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (name) clearErrors(name as Parameters<typeof clearErrors>[0]);
    });
    return () => subscription.unsubscribe();
  }, [watch, clearErrors]);

  const hasShownErrorToastRef = useRef(false);
  useEffect(() => {
    hasShownErrorToastRef.current = false;
  }, [id, massagerId]);
  useEffect(() => {
    if ((locationQuery.isError || massagerDetailQuery.isError) && !hasShownErrorToastRef.current) {
      const err = locationQuery.error ?? massagerDetailQuery.error;
      if (err && !isNotFoundError(err)) {
        hasShownErrorToastRef.current = true;
        toast.error(getApiErrorMessage(err, DEFAULT_FETCH_ERROR_MESSAGE));
      }
    }
  }, [
    locationQuery.isError,
    locationQuery.error,
    massagerDetailQuery.isError,
    massagerDetailQuery.error,
    toast,
  ]);

  useEffect(() => {
    if (r2Assignment || a2Assignment) {
      const sharedRemarks = r2Assignment?.remarks ?? a2Assignment?.remarks ?? null;
      form.reset({
        r2: r2Assignment
          ? {
              ...r2Assignment,
              available_from: r2Assignment.available_from ?? null,
              available_to: r2Assignment.available_to ?? null,
            }
          : defaultServiceValues,
        a2: a2Assignment
          ? {
              ...a2Assignment,
              available_from: a2Assignment.available_from ?? null,
              available_to: a2Assignment.available_to ?? null,
            }
          : defaultServiceValues,
        remarks: sharedRemarks,
      });
    }
  }, [r2Assignment, a2Assignment, form]);

  const scheduleRows: ScheduleRow[] = [
    {
      key: 'am',
      label: '午前',
      startKey: 'am_time_start',
      endKey: 'am_time_end',
      days: {
        monday: 'is_am_monday',
        tuesday: 'is_am_tuesday',
        wednesday: 'is_am_wednesday',
        thursday: 'is_am_thursday',
        friday: 'is_am_friday',
        saturday: 'is_am_saturday',
        sunday: 'is_am_sunday',
      },
    },
    {
      key: 'pm',
      label: '午後',
      startKey: 'pm_time_start',
      endKey: 'pm_time_end',
      days: {
        monday: 'is_pm_monday',
        tuesday: 'is_pm_tuesday',
        wednesday: 'is_pm_wednesday',
        thursday: 'is_pm_thursday',
        friday: 'is_pm_friday',
        saturday: 'is_pm_saturday',
        sunday: 'is_pm_sunday',
      },
    },
  ];

  const getScheduleColumns = (prefix: 'r2' | 'a2', disabled: boolean) => {
    const renderTimeDropdown = (
      field: Pick<
        ControllerRenderProps<
          ClinicLocationMassagerScheduleFormData,
          Path<ClinicLocationMassagerScheduleFormData>
        >,
        'value' | 'onChange'
      >
    ) => {
      const timeValue = typeof field.value === 'string' || field.value == null ? field.value : null;
      const parts = timeToParts(timeValue);
      const selectedHour = parts?.hour ?? '';
      const selectedMinute = parts?.minute ?? '';

      const handleHourChange = (value: unknown) => {
        const rawValue = value as TimeSelectValue;
        const hour = rawValue === '' || rawValue == null ? null : Number(rawValue);
        const minute = parts?.minute ?? 0;
        field.onChange(partsToTime(hour, minute, { withSeconds: true }));
      };

      const handleMinuteChange = (value: unknown) => {
        const rawValue = value as TimeSelectValue;
        const minute = rawValue === '' || rawValue == null ? null : Number(rawValue);
        const hour = parts?.hour ?? 0;
        field.onChange(partsToTime(hour, minute, { withSeconds: true }));
      };

      return (
        <div className="flex items-center gap-1">
          <AppSelect
            className="min-w-[56px]"
            options={HOUR_OPTIONS}
            value={selectedHour}
            onChange={handleHourChange}
            disabled={disabled}
          />
          <span className="text-gray-400">:</span>
          <AppSelect
            className="min-w-[56px]"
            options={MINUTE_OPTIONS}
            value={selectedMinute}
            onChange={handleMinuteChange}
            disabled={disabled}
          />
        </div>
      );
    };

    const columns: TableProps<ScheduleRow>['columns'] = [
      {
        title: '',
        dataIndex: 'label',
        key: 'label',
        width: 80,
        render: (text: string) => <span className="font-medium text-gray-700">{text}</span>,
      },
      {
        title: '開始',
        key: 'start',
        width: 180,
        render: (_, record) => (
          <Controller
            name={`${prefix}.${record.startKey}` as Path<ClinicLocationMassagerScheduleFormData>}
            control={form.control}
            render={({ field }) => renderTimeDropdown(field)}
          />
        ),
      },
      {
        title: '終了',
        key: 'end',
        width: 180,
        render: (_, record) => (
          <Controller
            name={`${prefix}.${record.endKey}` as Path<ClinicLocationMassagerScheduleFormData>}
            control={form.control}
            render={({ field }) => {
              return renderTimeDropdown(field);
            }}
          />
        ),
      },
      ...WEEKDAY_LABELS_WITH_KEY.map(({ key, label }) => ({
        title: label,
        key,
        align: 'center' as const,
        render: (_: unknown, record: ScheduleRow) => (
          <Controller
            name={`${prefix}.${record.days[key]}` as Path<ClinicLocationMassagerScheduleFormData>}
            control={form.control}
            render={({ field }) => (
              <AppCheckbox
                checked={!!field.value}
                onChange={e => field.onChange(e.target.checked)}
                disabled={disabled}
                aria-label={label}
              />
            )}
          />
        ),
      })),
    ];
    return columns;
  };

  const handleSubmit = form.handleSubmit(
    async values => {
      if (locationId == null || massagerIdNum == null) return;

      const promises = [];

      if (r2Enabled) {
        promises.push(
          updateMutation.mutateAsync({
            siteType,
            clinicLocationId: locationId,
            massagerId: massagerIdNum,
            payload: {
              ...values.r2,
              service_type: SERVICE_TYPE_R2,
              available_from: dateValueToIso(values.r2.available_from) ?? null,
              available_to: dateValueToIso(values.r2.available_to) ?? null,
              remarks: values.remarks ?? null,
              am_time_start: values.r2.am_time_start ?? null,
              am_time_end: values.r2.am_time_end ?? null,
              pm_time_start: values.r2.pm_time_start ?? null,
              pm_time_end: values.r2.pm_time_end ?? null,
            },
          })
        );
      }

      if (a2Enabled) {
        promises.push(
          updateMutation.mutateAsync({
            siteType,
            clinicLocationId: locationId,
            massagerId: massagerIdNum,
            payload: {
              ...values.a2,
              service_type: SERVICE_TYPE_A2,
              available_from: dateValueToIso(values.a2.available_from) ?? null,
              available_to: dateValueToIso(values.a2.available_to) ?? null,
              remarks: values.remarks ?? null,
              am_time_start: values.a2.am_time_start ?? null,
              am_time_end: values.a2.am_time_end ?? null,
              pm_time_start: values.a2.pm_time_start ?? null,
              pm_time_end: values.a2.pm_time_end ?? null,
            },
          })
        );
      }

      try {
        await Promise.all(promises);
        toast.success('拠点施術者を更新しました。');
        navigate(`/${siteType}/admin/clinic_locations/${locationId}`);
      } catch (err) {
        if (isNotFoundError(err)) {
          setShowNotFound(true);
        } else {
          toast.error(getApiErrorMessage(err, 'エラーが発生しました。'));
        }
      }
    }
  );

  const handleDelete = async () => {
    if (locationId == null || massagerIdNum == null) return;
    try {
      await deleteMutation.mutateAsync({
        siteType,
        clinicLocationId: locationId,
        massagerId: massagerIdNum,
      });
    } catch (err) {
      if (isNotFoundError(err)) {
        navigate(`/${siteType}/admin/clinic_locations/${locationId}`);
        return;
      }
      const message = getApiErrorMessage(err, '削除に失敗しました。');
      if (message === MASSAGER_DELETE_VISIT_DATA_ERROR) {
        navigate(`/${siteType}/admin/clinic_locations/${locationId}`, {
          state: { errorBanner: message },
        });
        return;
      }
      throw err;
    }
    navigate(`/${siteType}/admin/clinic_locations/${locationId}`);
  };

  const handleBack = () => {
    if (locationId == null) return;
    navigate(`/${siteType}/admin/clinic_locations/${locationId}`);
  };

  if (locationQuery.isLoading || massagerDetailQuery.isLoading) {
    return <LoadingTemplate />;
  }

  if (
    showNotFound ||
    locationQuery.isError ||
    massagerDetailQuery.isError ||
    !locationData ||
    !massagerInfo ||
    locationId == null
  ) {
    return <SiteAdminNotFoundTemplate />;
  }

  const locationName = locationData.location_name ?? '';
  const rawLocationNameKana = locationData.location_name_kana ?? undefined;
  const locationNameKana =
    rawLocationNameKana && isValidKatakana(rawLocationNameKana) ? rawLocationNameKana : undefined;
  const locationAvailableFrom = locationData.available_from ?? '';

  return (
    <div className="p-6">
      <AppSpace orientation="vertical" className="w-full" size="large">
        <AppBreadcrumb items={breadcrumbItems} />

        <form onSubmit={handleSubmit}>
          <AppCard className="mb-4 w-full">
            <div className="mb-4">
              {managerWarningMsg && (
                <div className="mb-4 text-red-600 text-base font-bold">{managerWarningMsg}</div>
              )}
              <h4 className="text-base font-semibold text-gray-900">
                {locationName} ({locationNameKana ? `${locationNameKana}` : ''})
                {locationAvailableFrom ? convGtJDate(dayjs(locationAvailableFrom)) : ''} -{' '}
                {availableTo && availableTo !== AVAILABLE_TO_INDEFINITE
                  ? convGtJDate(dayjs(availableTo))
                  : ''}
              </h4>
            </div>

            <div className="mb-4 grid grid-cols-1 items-center gap-x-4 gap-y-1">
              <AppAdminFormLabel label="施術者" className="mb-0" />
              <div className="min-w-0">
                <AppSelect
                  disabled
                  className="w-full"
                  options={[
                    {
                      value: massagerInfo.massager_id,
                      label:
                        massagerInfo.massager_serial_number != null
                          ? `${massagerInfo.massager_serial_number}: ${massagerInfo.massager_name}`
                          : massagerInfo.massager_name,
                    },
                  ]}
                  value={massagerInfo.massager_id}
                />
              </div>
            </div>
          </AppCard>

          <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4 mb-4">
            {/* A2 Component */}
            <AppCard
              title="はり・きゅう師 "
              className={!a2Enabled ? 'opacity-60 grayscale-[0.5]' : ''}
            >
              <div className="grid grid-cols-1 items-center gap-x-4 gap-y-1 mb-6">
                <AppAdminFormLabel label="適用期間" required className="mb-0" />
                <div className="flex w-full items-start gap-2">
                  <div className="flex flex-col flex-1 gap-1">
                    <Controller
                      name="a2.available_from"
                      control={form.control}
                      render={({ field }) => (
                        <RA2NDatePicker
                          value={field.value}
                          onChange={e => field.onChange(e)}
                          disabled={!a2Enabled}
                          placeholder=""
                          error={a2Enabled && !!form.formState.errors.a2?.available_from}
                        />
                      )}
                    />
                    {a2Enabled && form.formState.errors.a2?.available_from && (
                      <span className="text-sm text-red-500">
                        {form.formState.errors.a2.available_from.message}
                      </span>
                    )}
                  </div>
                  <span className="pt-2 text-gray-500 whitespace-nowrap">から</span>
                  <div className="flex flex-col flex-1 gap-1">
                    <Controller
                      name="a2.available_to"
                      control={form.control}
                      render={({ field }) => (
                        <RA2NDatePicker
                          value={
                            field.value === AVAILABLE_TO_INDEFINITE ? null : (field.value ?? null)
                          }
                          onChange={e => field.onChange(e)}
                          placeholder=""
                          disabled={!a2Enabled}
                          error={a2Enabled && !!form.formState.errors.a2?.available_to}
                        />
                      )}
                    />
                    {a2Enabled && form.formState.errors.a2?.available_to && (
                      <span className="text-sm text-red-500">
                        {form.formState.errors.a2.available_to.message}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto mb-6">
                <AppTable
                  columns={getScheduleColumns('a2', !a2Enabled)}
                  dataSource={scheduleRows}
                  pagination={false}
                  className="mb-6"
                />
              </div>
            </AppCard>
            {/* R2 Component */}
            <AppCard
              title="マッサージ師"
              className={!r2Enabled ? 'opacity-60 grayscale-[0.5]' : ''}
            >
              <div className="grid grid-cols-1 items-center gap-x-4 gap-y-1 mb-6">
                <AppAdminFormLabel label="適用期間" required className="mb-0" />
                <div className="flex w-full items-start gap-2">
                  <div className="flex flex-col flex-1 gap-1">
                    <Controller
                      name="r2.available_from"
                      control={form.control}
                      render={({ field }) => (
                        <RA2NDatePicker
                          value={field.value}
                          onChange={field.onChange}
                          disabled={!r2Enabled}
                          placeholder=""
                          error={r2Enabled && !!form.formState.errors.r2?.available_from}
                        />
                      )}
                    />
                    {r2Enabled && form.formState.errors.r2?.available_from && (
                      <span className="text-sm text-red-500">
                        {form.formState.errors.r2.available_from.message}
                      </span>
                    )}
                  </div>
                  <span className="pt-2 text-gray-500 whitespace-nowrap">から</span>
                  <div className="flex flex-col flex-1 gap-1">
                    <Controller
                      name="r2.available_to"
                      control={form.control}
                      render={({ field }) => (
                        <RA2NDatePicker
                          value={
                            field.value === AVAILABLE_TO_INDEFINITE ? null : (field.value ?? null)
                          }
                          onChange={field.onChange}
                          placeholder=""
                          disabled={!r2Enabled}
                          error={r2Enabled && !!form.formState.errors.r2?.available_to}
                        />
                      )}
                    />
                    {r2Enabled && form.formState.errors.r2?.available_to && (
                      <span className="text-sm text-red-500">
                        {form.formState.errors.r2.available_to.message}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto mb-6">
                <AppTable
                  columns={getScheduleColumns('r2', !r2Enabled)}
                  dataSource={scheduleRows}
                  pagination={false}
                  className="mb-6"
                />
              </div>
            </AppCard>
          </div>

          {/* 備考: shared for both A2 and R2 (not per-service) */}
          <AppCard className="mb-4 w-full">
            <div className="grid grid-cols-1 items-start gap-x-4 gap-y-1 sm:grid-cols-[120px_minmax(280px,1fr)]">
              <label className="pt-2 text-sm font-medium text-gray-700 sm:pt-2.5">備考</label>
              <div className="min-w-0 flex-1">
                <Controller
                  name="remarks"
                  control={form.control}
                  render={({ field }) => (
                    <div className="flex flex-col gap-1">
                      <AppTextArea
                        {...field}
                        value={field.value ?? ''}
                        onChange={e => field.onChange(e.target.value || null)}
                        disabled={!r2Enabled && !a2Enabled}
                        rows={4}
                        status={form.formState.errors.remarks ? 'error' : undefined}
                      />
                      {form.formState.errors.remarks && (
                        <span className="text-sm text-red-500">
                          {form.formState.errors.remarks.message}
                        </span>
                      )}
                    </div>
                  )}
                />
              </div>
            </div>
          </AppCard>

          <div className="flex flex-wrap gap-3 pt-2">
            <AppButton
              type="primary"
              htmlType="submit"
              disabled={form.formState.isSubmitting}
              loading={form.formState.isSubmitting}
              className="min-w-[120px]"
            >
              更新
            </AppButton>
            <AppButton
              type="default"
              danger
              className="min-w-[120px]"
              disabled={deleteMutation.isPending || form.formState.isSubmitting}
              onClick={() => {
                if (locationId == null || massagerIdNum == null) return;
                const modalInstance = openStandardDeleteConfirm({
                  okButtonProps: { loading: deleteMutation.isPending },
                  onOk: async () => {
                    try {
                      await handleDelete();
                    } catch (err) {
                      toast.error(getApiErrorMessage(err, 'エラーが発生しました。'));
                      modalInstance.destroy();
                      if (locationId != null) {
                        navigate(`/${siteType}/admin/clinic_locations/${locationId}`);
                      }
                    }
                  },
                });
              }}
            >
              削除
            </AppButton>
            <AppButton
              type="default"
              onClick={handleBack}
              className="min-w-[120px]"
              disabled={updateMutation.isPending || deleteMutation.isPending}
            >
              詳細画面へ戻る
            </AppButton>
          </div>
        </form>
      </AppSpace>
    </div>
  );
}
